import React, { useState } from 'react';
import { Dog, Heart, Sparkles, Sliders, CheckCircle2, Home, Cpu, RefreshCw, Zap } from 'lucide-react';
import confetti from 'canvas-confetti';
import { portfolioData } from '../data/portfolioData';

export default function PersonaWidgets() {
  const { copilot, sourMeter } = portfolioData;

  // Dog Co-Pilot State
  const [treatsGiven, setTreatsGiven] = useState(12);
  const [wagging, setWagging] = useState(false);

  // Sour Candy Fuel State
  const [fuel, setFuel] = useState(sourMeter.fuelLevel);

  // Tidy Home Tab State
  const [activeSetupTab, setActiveSetupTab] = useState('smart-home');

  const handleGiveTreat = () => {
    setTreatsGiven(prev => prev + 1);
    setWagging(true);
    setTimeout(() => setWagging(false), 1200);

    // Trigger sweet confetti burst
    confetti({
      particleCount: 25,
      spread: 60,
      origin: { y: 0.7 },
      colors: ['#C49A56', '#1E3A5F', '#F59E0B']
    });
  };

  const handleRefillSour = () => {
    setFuel(100);
    confetti({
      particleCount: 50,
      spread: 80,
      origin: { y: 0.7 },
      colors: ['#EC4899', '#8B5CF6', '#10B981', '#F59E0B']
    });
  };

  const setupFeatures = {
    'smart-home': [
      { title: "Home Assistant Hub", desc: "Local control for lighting, HVAC, and ambient mountain views." },
      { title: "Automated Routine", desc: "One-click 'Focus Mode' dims lights, tunes lofi audio, and powers studio monitors." },
      { title: "Energy Dashboard", desc: "Real-time power monitoring keeping energy waste at zero." }
    ],
    'workstation': [
      { title: "Tidy Desk Policy", desc: "Zero loose wires, hidden dual-monitor arms, integrated wireless pads." },
      { title: "Ergonomic Balance", desc: "Standing desk transition triggers with hourly stretch reminders." },
      { title: "Acoustic Prep", desc: "Sound-dampening panels for crisp audio recordings and call clarity." }
    ],
    'cable-layout': [
      { title: "Under-Desk Trays", desc: "Raceway cable tracks routing power bricks completely out of sight." },
      { title: "Custom Braided Sleeves", desc: "Color-matched slate blue and gold accent cable wraps." },
      { title: "Modular Quick-Disconnect", desc: "Easy swap mounts for mechanical keyboards and testing rigs." }
    ]
  };

  return (
    <section id="persona" className="py-20 bg-slate-100/80 dark:bg-slate-950/60 border-y border-slate-200 dark:border-slate-800/60 relative transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-bronze/10 border border-brand-bronze/30 text-brand-bronze text-xs font-mono">
            <Sparkles className="w-3.5 h-3.5" /> PERSONAL DNA & HABITS
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">
            Driven by Passion, Dogs, & Sour Candy
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-base">
            What makes me tick? A glimpse into the daily fuel, co-pilot morale, and tidy environment that powers every project.
          </p>
        </div>

        {/* 3 Grid Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* CARD 1: Dog Co-Pilot Spotlight */}
          <div className="rounded-3xl bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-800 p-6 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-lg dark:shadow-xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-5 text-amber-400 group-hover:opacity-10 transition-opacity">
              <Dog className="w-32 h-32" />
            </div>

            <div className="space-y-4 relative z-10">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-2xl bg-amber-500/15 text-amber-400 border border-amber-500/30 transition-transform ${wagging ? 'animate-bounce' : ''}`}>
                    <Dog className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                      Pierre & Leroy
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-300 font-mono">Frenchie Duo</span>
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 font-mono">{copilot.title}</p>
                  </div>
                </div>
              </div>

              {/* Individual Dogs Cards */}
              <div className="grid grid-cols-2 gap-2.5">
                {copilot.dogs.map(dog => (
                  <div key={dog.name} className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-950/90 border border-slate-200 dark:border-slate-800 space-y-1 hover:border-amber-500/40 transition-colors">
                    <div className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1">
                      <span>{dog.emoji}</span>
                      <span>{dog.name}</span>
                    </div>
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 font-mono">{dog.breed}</p>
                    <div className="flex flex-wrap gap-1 pt-1">
                      {dog.traits.slice(0, 2).map(t => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-800">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Combined Morale Stats */}
              <div className="p-3.5 rounded-2xl bg-slate-100 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-2">
                <div className="flex justify-between items-center text-xs font-mono">
                  <span className="text-slate-600 dark:text-slate-400 flex items-center gap-1">
                    <Heart className="w-3.5 h-3.5 text-red-500 fill-red-500" /> Combined Morale:
                  </span>
                  <span className="text-amber-600 dark:text-amber-400 font-bold">200/10 Frenchie Duo</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-500 via-amber-400 to-amber-300 h-full w-full rounded-full"></div>
                </div>
                <p className="text-[11px] italic text-slate-700 dark:text-slate-300">
                  "{copilot.favoriteActivity}"
                </p>
              </div>

            </div>

            {/* Interactive Treat Button */}
            <div className="pt-5 border-t border-slate-800 flex items-center justify-between mt-4 relative z-10">
              <div className="text-xs font-mono text-slate-400">
                Treats Served: <span className="text-amber-400 font-bold text-sm">{treatsGiven}</span>
              </div>
              <button
                onClick={handleGiveTreat}
                className="px-3.5 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 font-medium text-xs flex items-center gap-1.5 transition-all active:scale-95"
              >
                🦴 Treat Pierre & Leroy
              </button>
            </div>
          </div>

          {/* CARD 2: Sour Candy Fuel Meter */}
          <div className="rounded-3xl bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-800 p-6 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-lg dark:shadow-xl relative overflow-hidden group">
            <div className="space-y-4">
              
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-2xl bg-pink-500/15 text-pink-500 dark:text-pink-400 border border-pink-500/30">
                    <Zap className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900 dark:text-white">Sour Fuel Gauge</h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 font-mono">Productivity Power Source</p>
                  </div>
                </div>
                <button 
                  onClick={handleRefillSour} 
                  className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
                  title="Refill Sour Stash"
                >
                  <RefreshCw className="w-4 h-4 text-pink-500 dark:text-pink-400" />
                </button>
              </div>

              {/* Meter Gauge Visual */}
              <div className="p-4 rounded-2xl bg-slate-100 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-3">
                <div className="flex justify-between items-center text-xs font-mono">
                  <span className="text-slate-600 dark:text-slate-400">Sour Patch Level:</span>
                  <span className="text-pink-600 dark:text-pink-400 font-bold text-sm">{fuel}% Fuel</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-800 h-3 rounded-full overflow-hidden p-0.5">
                  <div 
                    className="bg-gradient-to-r from-pink-500 via-purple-500 to-amber-400 h-full rounded-full transition-all duration-300"
                    style={{ width: `${fuel}%` }}
                  ></div>
                </div>
                <input 
                  type="range" 
                  min="10" 
                  max="100" 
                  value={fuel} 
                  onChange={(e) => setFuel(Number(e.target.value))}
                  className="w-full accent-pink-500 bg-slate-200 dark:bg-slate-800 h-1 rounded-lg cursor-pointer"
                />
              </div>

              {/* Candy Details */}
              <div className="space-y-2 text-xs">
                <div className="flex justify-between text-slate-600 dark:text-slate-400 font-mono">
                  <span>Stash Favorite:</span>
                  <span className="text-slate-900 dark:text-slate-200 font-medium">{sourMeter.favorite}</span>
                </div>
                <div className="p-3 rounded-xl bg-pink-500/10 dark:bg-pink-950/20 border border-pink-500/20 dark:border-pink-900/30 text-pink-700 dark:text-pink-200 italic">
                  "{fuel > 80 ? sourMeter.quote : fuel > 40 ? "Optimal sourness achieved. Project code flowing smoothly." : "Warning: Sour levels dangerously low! Please restock candy."}"
                </div>
              </div>

            </div>

            <div className="pt-6 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between mt-4">
              <span className="text-xs text-slate-600 dark:text-slate-400 font-mono">Status: {fuel > 50 ? "⚡ Fully Charged" : "🪫 Needs Candy"}</span>
              <button
                onClick={handleRefillSour}
                className="px-4 py-2 rounded-xl bg-pink-500/15 hover:bg-pink-500/25 border border-pink-500/30 text-pink-600 dark:text-pink-300 font-medium text-xs flex items-center gap-1.5 transition-all"
              >
                🍬 Restock Stash
              </button>
            </div>
          </div>

          {/* CARD 3: Tidy Home & Setup Showcase */}
          <div className="rounded-3xl bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-800 p-6 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-lg dark:shadow-xl">
            <div className="space-y-4">
              
              {/* Header */}
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-2xl bg-brand-navy/10 dark:bg-brand-navy/60 text-brand-navy dark:text-blue-400 border border-blue-500/30">
                  <Home className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900 dark:text-white">Tidy Home Setup</h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400 font-mono">Clean Space, Clear Mind</p>
                </div>
              </div>

              {/* Tab Selector */}
              <div className="flex rounded-xl bg-slate-100 dark:bg-slate-950 p-1 border border-slate-200 dark:border-slate-800 text-xs font-medium">
                <button
                  onClick={() => setActiveSetupTab('smart-home')}
                  className={`flex-1 py-1.5 rounded-lg transition-colors ${activeSetupTab === 'smart-home' ? 'bg-brand-navy text-white shadow' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  Smart Home
                </button>
                <button
                  onClick={() => setActiveSetupTab('workstation')}
                  className={`flex-1 py-1.5 rounded-lg transition-colors ${activeSetupTab === 'workstation' ? 'bg-brand-navy text-white shadow' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  Workstation
                </button>
                <button
                  onClick={() => setActiveSetupTab('cable-layout')}
                  className={`flex-1 py-1.5 rounded-lg transition-colors ${activeSetupTab === 'cable-layout' ? 'bg-brand-navy text-white shadow' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  Cable Layout
                </button>
              </div>

              {/* Dynamic Feature Checklist */}
              <div className="space-y-2.5 pt-1">
                {setupFeatures[activeSetupTab].map((item, idx) => (
                  <div key={idx} className="p-3 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800/80 flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-200">{item.title}</h4>
                      <p className="text-xs text-slate-600 dark:text-slate-400 leading-snug">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            <div className="pt-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-400 font-mono flex items-center justify-between mt-4">
              <span>Organization: 100%</span>
              <span className="text-brand-bronze font-semibold">Minimalist Standard</span>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
